import {FC, useEffect} from 'react';
import classNames from 'classnames';
import {ReactComponent as Clock} from '../../../assets/images/clock.svg';
import Intervals from './Intervals';
import {getStampPosition} from './helpers/getStampPosition';
import {getState} from './helpers/getState';
import {
  ProgressBarState, PERCENT_RATE, EXPIRED_TIME_INDENT,
  MAX_STAMP_POSITION, MIN_EXPIRED_TIME_STAMP_POSITION
} from '../../../constants/timeProgressBar';

type TimeProgressBarProps = {
  currentTimeInSeconds?: Nullable<number>,
  targetTimeInSeconds?: Nullable<number>,
  alertRangeInSeconds?: Nullable<number>,
  shouldShowExpiredIcon?: boolean,
  isCurrentlyRunning?: boolean,
  hasIntervals?: boolean,
  isWide?: boolean,
  onChangeStateCallback?: (state: string) => void,
}

const TimeProgressBar: FC<TimeProgressBarProps> = ({
  currentTimeInSeconds = 0,
  targetTimeInSeconds = 0,
  alertRangeInSeconds = 0,
  onChangeStateCallback,
  shouldShowExpiredIcon = true,
  isCurrentlyRunning = false,
  hasIntervals = false,
  isWide = false,
  children
}) => {
  const currentTime = currentTimeInSeconds || 0;
  const targetTime = targetTimeInSeconds || 0;
  const alertRange = alertRangeInSeconds || 0;

  const isIntervalProgressbar = hasIntervals && isCurrentlyRunning;

  const max = targetTime + alertRange;
  const value = Math.round((currentTime / max) * PERCENT_RATE);
  const resultValue = value > PERCENT_RATE ? PERCENT_RATE : value;
  const stampPosition = getStampPosition(targetTime, max, currentTime);
  const expiredTimePosition = MAX_STAMP_POSITION - stampPosition;
  const currentExpiredTimePosition =
  stampPosition < MIN_EXPIRED_TIME_STAMP_POSITION ?
  expiredTimePosition - EXPIRED_TIME_INDENT : expiredTimePosition;

  const shouldShowIntervals = isIntervalProgressbar &&
    (currentTime >= max);

  const {state, color} = getState({
    currentTime, alertRange, targetTime, isCurrentlyRunning, shouldShowIntervals
  });

  useEffect(() => {
    if (onChangeStateCallback && state) {
      onChangeStateCallback(state);
    }
  }, [onChangeStateCallback, state]);

  return (
    <div className={classNames('time-progress-bar', {
      'wide': isWide,
      'interval-time-progress-bar': isIntervalProgressbar
    })}>
      <div
        className={classNames('time-progress-bar__wrapper',
            {'white': isCurrentlyRunning && !children}
        )}
      >
        <div
          className={`time-progress-bar__value ${color}`}
          style={{width: `${resultValue}%`}}
        />

        {children && (
          <div
            className={`time-progress-bar__expired-time`}
            style={{right: `${currentExpiredTimePosition}%`}}
          >
            {children}
          </div>
        )}

        <div
          className="time-progress-bar__stamp"
          style={{left: `${stampPosition}%`}}
        />
      </div>

      {shouldShowIntervals && (
        <Intervals currentTime={currentTime} max={max} />
      )}
      {shouldShowExpiredIcon && state === ProgressBarState.EXPIRED && <Clock/>}
    </div>
  );
};

export default TimeProgressBar;
